/**
 * CapBoard — kanban over the CAP lifecycle (Asana Board pattern; fixed
 * lifecycle columns, native HTML5 drag — no library). Dropping a card on a
 * column advances the CAP through PATCH /api/fdr/corrective-actions/:id, so
 * the board is a projection of the same records as the list: one store, two
 * views. Backward drags are refused server-visibly (we simply don't call).
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

const COLUMNS = [
  ['draft', 'Draft'], ['submitted', 'Submitted'], ['approved', 'Approved'],
  ['in_progress', 'Implementing'], ['completed', 'Completed'], ['verified', 'Verified'],
];
const ORDER = Object.fromEntries(COLUMNS.map(([k], i) => [k, i]));
const COL_TINT = {
  draft: 'border-slate-300', submitted: 'border-blue-400', approved: 'border-indigo-400',
  in_progress: 'border-amber-400', completed: 'border-blue-500', verified: 'border-emerald-500',
};

export default function CapBoard({ onOpenEntity }) {
  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = useCallback(async () => {
    try {
      // Findings list carries cap_count; detail carries CAPs — one round trip
      // per finding is too chatty for a board, so use the findings list and
      // fetch details for those with CAPs, bounded.
      const res = await fetch(`${API}/api/fdr/findings`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) return;
      const withCaps = (data.findings || []).filter(f => f.cap_count > 0).slice(0, 30);
      const details = await Promise.all(withCaps.map(f =>
        fetch(`${API}/api/fdr/findings/${f.id}`, { credentials: 'include' }).then(r => r.json()).catch(() => null)));
      const all = [];
      for (const d of details) {
        if (!d?.success) continue;
        for (const ca of d.corrective_actions || []) {
          all.push({ ...ca, finding_title: d.finding.title, entity_id: d.finding.entity_id, entity_name: d.finding.entity_name });
        }
      }
      setCaps(all);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (capId, toStatus) => {
    const cap = caps.find(c => c.id === capId);
    if (!cap || ORDER[toStatus] === undefined) return;
    const from = ORDER[cap.status] ?? 3; // overdue ≈ implementing
    if (ORDER[toStatus] <= from) return; // forward-only, like the ladder
    setCaps(prev => prev.map(c => c.id === capId ? { ...c, status: toStatus } : c)); // optimistic
    await fetch(`${API}/api/fdr/corrective-actions/${capId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: toStatus }),
    }).catch(() => {});
    load();
  };

  if (loading) return <div className="p-12 text-center text-slate-400">Loading board…</div>;

  return (
    <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 420 }}>
      {COLUMNS.map(([key, label]) => {
        // 'overdue' is a due-date state, not a lifecycle stage — show it in Implementing.
        const colCards = caps.filter(c => c.status === key || (c.status === 'overdue' && key === 'in_progress'));
        return (
          <div key={key}
            onDragOver={e => { e.preventDefault(); setOverCol(key); }}
            onDragLeave={() => setOverCol(o => o === key ? null : o)}
            onDrop={e => { e.preventDefault(); setOverCol(null); if (dragId) advance(dragId, key); setDragId(null); }}
            className={`flex-shrink-0 w-60 rounded-2xl border-t-4 ${COL_TINT[key]} bg-slate-50/70 border border-slate-200/60 transition-colors ${overCol === key ? 'bg-orange-50' : ''}`}>
            <div className="px-3 py-2.5 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{label}</span>
              <span className="text-[11px] text-slate-400 font-semibold">{colCards.length}</span>
            </div>
            <div className="px-2 pb-2 space-y-2">
              {colCards.map(c => (
                <div key={c.id} draggable
                  onDragStart={() => setDragId(c.id)} onDragEnd={() => setDragId(null)}
                  className={`bg-white rounded-xl border border-slate-200/80 shadow-sm px-3 py-2.5 cursor-grab active:cursor-grabbing ${dragId === c.id ? 'opacity-40' : ''}`}>
                  <div className="text-xs font-semibold text-slate-800 leading-snug">{c.title}</div>
                  <div className="text-[10px] text-slate-400 mt-1 truncate">{c.finding_title}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <button onClick={() => onOpenEntity?.(c.entity_id)}
                      className="text-[10px] text-orange-600 hover:underline truncate max-w-[110px]">{c.entity_name}</button>
                    <span className={`text-[10px] font-semibold ${c.due_date && new Date(c.due_date) < new Date() && !['completed','verified'].includes(c.status) ? 'text-red-600' : 'text-slate-400'}`}>
                      {c.due_date ? String(c.due_date).split('T')[0] : ''}
                    </span>
                  </div>
                </div>
              ))}
              {colCards.length === 0 && <div className="text-[11px] text-slate-300 px-1.5 pb-2">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
